import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@mui/material/styles";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from "react-router-dom";
import { Button, TextField, Box, CircularProgress, Container } from "@material-ui/core";
import { format } from "date-fns";
import { Can } from "../../components/Can";
import KanbanBoard from "../../components/KanbanBoard";
import { buildKanbanLanes } from "../../utils/kanbanUtils";
import { Search as SearchIcon } from "lucide-react";
import PageTitle from "../../components/PageTitle";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  titleContainer: {
    paddingTop: theme.spacing(1),
  },
  headerActionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  filtersContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    minWidth: 0,
  },
  dateInput: {
    minWidth: "150px",
  },
  addColumnButton: {
    marginLeft: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  boardWrapper: {
  flex: 1,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",   // allow inner scroll container
  },
}));

/**
 * Optimized Kanban Page Component
 *
 * Performance optimizations:
 * 1. Memoized components prevent unnecessary re-renders
 * 2. useMemo for expensive calculations (buildKanbanLanes)
 * 3. useCallback for event handlers to maintain referential equality
 * 4. Debounced socket events to prevent excessive updates
 * 5. Efficient state management - only fetch when filters change
 * 6. Separate concerns: data fetching, transformation, and rendering
 */
const Kanban = () => {
  const classes = useStyles();
  const themeV5 = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  // State management
  const [tags, setTags] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [draggedCard, setDraggedCard] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Refs for debouncing socket events
  const socketDebounceTimer = useRef(null);
  const lastFetchTime = useRef(0);

  // Get queue IDs from user context
  const queueIds = useMemo(
    () => JSON.stringify(user.queues.map(queue => queue.UserQueue.queueId)),
    [user.queues]
  );

  /**
   * Fetch tags from API
   * Memoized to maintain referential equality
   */
  const fetchTags = useCallback(async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      return fetchedTags;
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error(i18n.t("error.fetchingTags"));
      return [];
    }
  }, []);

  /**
   * Fetch tickets from API
   * Optimized pagination and filtering at the API level
   */
  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds,
          startDate,
          endDate,
        },
      });
      setAllTickets(data.tickets || []);
      return data.tickets || [];
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error(i18n.t("error.fetchingTickets"));
      setAllTickets([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [queueIds, startDate, endDate]);

  /**
   * Initial data load: fetch tags on user context change
   */
  useEffect(() => {
    fetchTags();
  }, [user, fetchTags]);

  /**
   * Fetch tickets when date filters change
   */
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  /**
   * Debounced socket event listener
   * Prevents excessive re-fetches when multiple events fire in quick succession
   */
  useEffect(() => {
    const companyId = user.companyId;

    const handleSocketEvent = () => {
      // Debounce: only fetch if at least 1 second has passed since last fetch
      const now = Date.now();
      if (now - lastFetchTime.current < 1000) {
        // Clear existing timer and reschedule
        if (socketDebounceTimer.current) clearTimeout(socketDebounceTimer.current);
        socketDebounceTimer.current = setTimeout(() => {
          fetchTickets();
          lastFetchTime.current = Date.now();
        }, 1000);
        return;
      }

      lastFetchTime.current = now;
      fetchTickets();
    };

    if (socket && Object.keys(socket).length > 0 && companyId) {
      socket.on(`company-${companyId}-ticket`, handleSocketEvent);
      socket.on(`company-${companyId}-appMessage`, handleSocketEvent);

      return () => {
        if (socketDebounceTimer.current) clearTimeout(socketDebounceTimer.current);
        socket.off(`company-${companyId}-ticket`, handleSocketEvent);
        socket.off(`company-${companyId}-appMessage`, handleSocketEvent);
      };
    }

    return () => {
      if (socketDebounceTimer.current) clearTimeout(socketDebounceTimer.current);
    };
  }, [socket, user?.companyId, fetchTickets]);

  /**
   * Build lanes data with memoization
   * Only rebuilds when tickets or tags actually change
   */
  const filteredTickets = useMemo(() => {
    if (!searchTerm.trim()) {
      return allTickets;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return allTickets.filter(ticket => 
      ticket.contact?.name?.toLowerCase().includes(lowerSearch) ||
      ticket.contact?.number?.includes(searchTerm)
    );
  }, [allTickets, searchTerm]);

  const lanesData = useMemo(() => {
    return buildKanbanLanes(filteredTickets, tags);
  }, [filteredTickets, tags]);

  /**
   * Handle date filter changes
   */
  const handleStartDateChange = useCallback((event) => {
    setStartDate(event.target.value);
  }, []);

  const handleEndDateChange = useCallback((event) => {
    setEndDate(event.target.value);
  }, []);

  /**
   * Handle navigation to tag management
   */
  const handleAddColumnClick = useCallback(() => {
    history.push("/tagsKanban");
  }, [history]);

  /**
   * Handle card click - navigate to ticket
   */
  const handleCardClick = useCallback((uuid) => {
    history.push(`/tickets/${uuid}`);
  }, [history]);

  /**
   * Handle card drop - update ticket tag
   */
  const handleCardDrop = useCallback(async (cardId, sourceLaneId, targetLaneId) => {
    // Prevent dropping in same lane
    if (sourceLaneId === targetLaneId) {
      return;
    }

    try {
      // Remove old tag from ticket
      if (sourceLaneId !== "lane0") {
        // DELETE /ticket-tags/:ticketId - Remove ticket from old tag
        await api.delete(`/ticket-tags/${cardId}`);
        toast.success(i18n.t("kanban.messages.tagRemoved"));
      }

      // Add new tag to ticket
      if (targetLaneId !== "lane0") {
        // PUT /ticket-tags/:ticketId/:tagId - Add ticket to new tag
        // targetLaneId is the tag ID, cardId is the ticket ID
        await api.put(`/ticket-tags/${cardId}/${targetLaneId}`);
        toast.success(i18n.t("kanban.messages.tagAdded"));
      }

      // Refresh tickets after successful update
      await fetchTickets();
    } catch (error) {
      console.error("Error moving card:", error);
      toast.error(i18n.t("error.movingCard"));
      await fetchTickets();
    }
  }, [fetchTickets]);

  // Render loading state
  if (loading && allTickets.length === 0) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: themeV5.palette.background.default,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          px: 2,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
          overflowY: "hidden",
        }}
      >
        <div className={classes.root}>
          {/* Header and Filters */}
          <div className={classes.headerContainer}>
            <div className={classes.titleContainer}>
              <PageTitle sx={{ mb: 0 }}>{i18n.t("mainDrawer.listItems.kanban")}</PageTitle>
            </div>

            <div className={classes.headerActionsRow}>
              <div className={classes.filtersContainer}>
                <TextField
                  label={i18n.t("kanban.startDate")}
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  className={classes.dateInput}
                />
                <Box mx={0.5} />
                <TextField
                  label={i18n.t("kanban.endDate")}
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  className={classes.dateInput}
                />
                <Box mx={0.5} />
                <TextField
                  placeholder={i18n.t("kanban.searchPlaceholder")}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="outlined"
                  size="small"
                  className={classes.dateInput}
                  InputProps={{
                    startAdornment: <SearchIcon size={18} style={{ marginRight: 8 }} />
                  }}
                />
              </div>

              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddColumnClick}
                    className={classes.addColumnButton}
                  >
                    {i18n.t("kanban.buttons.addColumns")}
                  </Button>
                )}
              />
            </div>
          </div>

          {/* Kanban Board */}
          <div className={classes.boardWrapper}>
            <KanbanBoard
              lanes={lanesData}
              onCardClick={handleCardClick}
              onCardDrop={handleCardDrop}
            />
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default Kanban;
